// Pure reconcile logic for the berth Produkte the wizard seeds as Leistungen (#6734).
// Kept free of Angular/HTTP so the add / remove / re-quantify decisions are unit
// testable; the loading, pricing and signal plumbing lives in
// BookingV2BerthProductService.
//
// Mirrors the legacy dialog (booking-dialog-positions.addFromBerthAssignment /
// removeFromBerthAssignment): a berth's products become billable lines when the
// berth joins the booking, filtered by the berth product's optional Beziehung
// condition, with a stay-duration quantity for day/overnight units.

import { v4 as uuidv4 } from 'uuid';

import {
    filterBerthProductsForBooking,
    resolveBerthProductQuantityForBooking,
} from '@shared/utils/berth-product-booking.util';
import { Product } from '@shared/models/product';

import { BookingV2Service } from '../dtos/booking-v2.dtos';

// One berth taking part in the booking, with the window its lines are billed for
// and the stay duration already derived (the caller owns the tenant's day/overnight
// setting). `products` is null while the berth's catalog is still loading.
export interface BerthProductContext {
    berthId: number;
    from: Date | null;
    to: Date | null;
    openEnd: boolean;
    durationOfStay: number;
    products: readonly Product[] | null;
}

// Seeded lines are keyed by berth + product: at most one line per berth product,
// so a re-run never duplicates and a quantity edit is always re-found. Exported so
// the service can diff its own previous output against the current lines and spot
// a line the user deleted by hand.
export function berthProductLineKey(berthId: number, productId: number | null): string {
    return `${berthId}:${productId ?? ''}`;
}

const lineKey = berthProductLineKey;

/** The keys of the reconcile-managed lines currently in the booking. */
export function managedBerthProductKeys(services: readonly BookingV2Service[]): Set<string> {
    const keys = new Set<string>();
    for (const service of services) {
        const key = managedKey(service);
        if (key != null) {
            keys.add(key);
        }
    }
    return keys;
}

// Only a reconcile-managed line participates. A line recovered on edit carries a
// berthOrigin too (for its berth link) but `managed: false` — it is an ordinary
// editable service and must never be re-quantified or dropped from here.
function managedKey(service: BookingV2Service): string | null {
    return service.berthOrigin?.managed
        ? lineKey(service.berthOrigin.berthId, service.productId)
        : null;
}

// What the reconcile derives from the berth product + occupancy: the quantity and
// the Leistungszeitraum. Stored alongside as a signature so a later pass can tell
// its own output from a value the user typed.
interface DerivedLine {
    quantity: number;
    serviceStart: Date | null;
    serviceEnd: Date | null;
    serviceEndOpen: boolean;
}

function derive(product: Product, ctx: BerthProductContext): DerivedLine {
    return {
        quantity: resolveBerthProductQuantityForBooking(product, ctx.durationOfStay),
        // Leistungszeitraum = Belegungszeitraum (Bierdeckel/Bericht), as in legacy.
        serviceStart: ctx.from,
        serviceEnd: ctx.openEnd ? null : ctx.to,
        serviceEndOpen: ctx.openEnd,
    };
}

function signatureOf(line: {
    quantity: number;
    serviceStart: Date | null;
    serviceEnd: Date | null;
    serviceEndOpen?: boolean;
}): string {
    return [
        line.quantity,
        line.serviceStart?.getTime() ?? '',
        line.serviceEnd?.getTime() ?? '',
        !!line.serviceEndOpen,
    ].join('|');
}

function toService(
    berthId: number,
    product: Product,
    ctx: BerthProductContext,
): BookingV2Service {
    const derived = derive(product, ctx);
    return {
        id: uuidv4(),
        productId: product.id,
        name: product.name,
        category: product.category?.name ?? null,
        quantity: derived.quantity,
        unitId: product.unit?.id ?? null,
        unitLabelKey: product.unit?.translationKey ?? null,
        // Catalog base — a rule-priced product is re-priced against the booking
        // context by BookingV2ServicePricingService, exactly like a hand-added line.
        price: product.price,
        taxRateId: product.taxRate?.id ?? null,
        vatValue: product.taxRate?.value ?? 0,
        hasDynamicPrice: !!product.hasDynamicPrice,
        requiredEntities: product.priceRuleMap?.getRequirements?.() ?? [],
        tenantRelationCriteriaIds: product.priceRuleMap?.getTenantRelationCriteriaIds?.() ?? [],
        pricingRelationId: null,
        dynamicRuleName: null,
        autoName: product.name,
        dynamicPrice: null,
        serviceStart: derived.serviceStart,
        serviceEnd: derived.serviceEnd,
        serviceEndOpen: derived.serviceEndOpen,
        berthOrigin: {
            berthId,
            managed: true,
            ignoreFreeOfChargePosition: !!product.ignoreFreeOfChargePosition,
            seededSignature: signatureOf(derived),
        },
    };
}

/**
 * Folds the berth Produkte of every participating berth into the booking's service
 * lines: adds what is missing, drops a line whose berth left the booking or whose
 * Beziehung condition no longer matches, and re-derives the stay-duration quantity
 * of a line the user has not edited. Non-berth lines are returned untouched, in
 * order. Returns the same array reference when nothing changed, so the caller can
 * skip a no-op signal write.
 */
export function reconcileBerthProductServices(
    services: readonly BookingV2Service[],
    contexts: readonly BerthProductContext[],
    tenantRelationId: number | null,
    suppressedKeys: ReadonlySet<string> = new Set(),
): readonly BookingV2Service[] {
    // Berths whose catalog has not arrived yet keep their existing lines and
    // contribute none — otherwise a pending request would wipe them and re-add.
    const pending = new Set(contexts.filter(c => c.products == null).map(c => c.berthId));
    const wanted = new Map<string, { berthId: number; product: Product; ctx: BerthProductContext }>();

    for (const ctx of contexts) {
        if (!ctx.products) {
            continue;
        }
        for (const product of filterBerthProductsForBooking(ctx.products, tenantRelationId)) {
            const key = lineKey(ctx.berthId, product.id);
            // The user deleted this line in the Leistungen tab — never bring it back.
            if (suppressedKeys.has(key)) {
                continue;
            }
            wanted.set(key, { berthId: ctx.berthId, product, ctx });
        }
    }

    let changed = false;
    const kept: BookingV2Service[] = [];
    const seen = new Set<string>();

    for (const service of services) {
        const key = managedKey(service);
        if (key == null) {
            kept.push(service);
            continue;
        }

        // A settled line is never touched — it is already on an invoice/receipt.
        if (service.isProtected) {
            kept.push(service);
            seen.add(key);
            continue;
        }

        if (pending.has(service.berthOrigin.berthId)) {
            kept.push(service);
            seen.add(key);
            continue;
        }

        const match = wanted.get(key);
        if (!match) {
            changed = true;
            continue;
        }

        seen.add(key);
        kept.push(resync(service, match.ctx, match.product));
        if (kept[kept.length - 1] !== service) {
            changed = true;
        }
    }

    // Edit: a berth position already recovered from the saved booking covers this
    // product — seeding it again would bill the same Leistung twice (e.g. swapping
    // to another berth that carries the same standard Liegeplatz-Produkt).
    const recoveredProductIds = new Set(
        services
            .filter(s => s.berthOrigin && !s.berthOrigin.managed && s.productId != null)
            .map(s => s.productId as number),
    );

    for (const [key, { berthId, product, ctx }] of wanted) {
        if (recoveredProductIds.has(product.id)) {
            continue;
        }
        if (!seen.has(key)) {
            kept.push(toService(berthId, product, ctx));
            changed = true;
        }
    }

    return changed ? kept : services;
}

// A changed occupancy window re-derives quantity and Leistungszeitraum — but only
// for a line that still matches what the reconcile last wrote. The moment the user
// edits either, the signature stops matching and the line is left alone for good.
function resync(
    service: BookingV2Service,
    ctx: BerthProductContext,
    product: Product,
): BookingV2Service {
    const current = signatureOf(service);
    if (current !== service.berthOrigin.seededSignature) {
        return service;
    }

    const derived = derive(product, ctx);
    const next = signatureOf(derived);
    if (next === current) {
        return service;
    }

    return {
        ...service,
        ...derived,
        berthOrigin: { ...service.berthOrigin, seededSignature: next },
    };
}
